import { formatMoney, formatPercent } from "@/lib/prefs";
import { TrendingUp, TrendingDown, HandCoins, Trash2, UtensilsCrossed } from "lucide-react";

export default function OwnerReport({ data }) {
  if (!data) return null;
  const { sales, deductions, totals } = data;
  const positive = totals.net_margin >= 0;

  return (
    <section className="rounded-2xl glass-card overflow-hidden" data-testid="owner-report">
      <div className="p-6 border-b border-border flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow gold">Owner's Report • last {data.days} days</div>
          <h2 className="font-display text-3xl mt-1">Sales vs Food Cost</h2>
        </div>
        <div className="text-right">
          <div className="label-eyebrow text-[0.55rem]">Net margin</div>
          <div className={`font-display text-4xl tabular ${positive ? "gold" : "text-destructive"}`} data-testid="owner-net-margin">
            {formatMoney(totals.net_margin)}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 border-b border-border divide-y md:divide-y-0 md:divide-x divide-border">
        <Cell label="Sales revenue" value={formatMoney(sales.revenue)} sub={`${sales.units_sold} units`} icon={TrendingUp} testId="owner-revenue" positive />
        <Cell label="Sales food cost" value={formatMoney(sales.food_cost)} sub={`Labor ${formatMoney(sales.labor_cost)}`} icon={TrendingDown} testId="owner-food-cost" />
        <Cell label="Total food cost" value={formatMoney(totals.total_food_cost)} sub={`${formatPercent(totals.food_cost_percent, 1)} of revenue`} icon={TrendingDown} testId="owner-total-food" />
        <Cell label="Gross margin" value={formatMoney(sales.gross_margin)} sub="Before deductions" testId="owner-gross" />
      </div>

      <div className="p-6">
        <div className="label-eyebrow mb-3">Profit lost to</div>
        <div className="grid md:grid-cols-3 gap-3">
          <LossCard icon={HandCoins} label="Comps" foodCost={deductions.comps.food_cost} extra={`Revenue lost ${formatMoney(deductions.comps.revenue_lost)}`} testId="owner-comps" />
          <LossCard icon={Trash2} label="Spillage / Waste" foodCost={deductions.spillage.food_cost} testId="owner-spillage" />
          <LossCard icon={UtensilsCrossed} label="Staff meals" foodCost={deductions.staff_meals.food_cost} testId="owner-staff-meals" />
        </div>
      </div>
    </section>
  );
}

function Cell({ label, value, sub, icon: Icon, testId, positive }) {
  return (
    <div className="p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon className={`w-3.5 h-3.5 ${positive ? "gold" : "text-muted-foreground"}`} />}
      </div>
      <div className="font-display text-3xl tabular">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function LossCard({ icon: Icon, label, foodCost, extra, testId }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-destructive" />
        <div className="label-eyebrow text-destructive text-[0.6rem]">{label}</div>
      </div>
      <div className="font-display text-2xl tabular text-destructive">{formatMoney(foodCost)}</div>
      {extra && <div className="text-xs text-muted-foreground mt-1">{extra}</div>}
    </div>
  );
}
